<template>
    <div class="p-6 space-y-8 my-32 bg-white min-h-screen">
        <!-- Titre -->
        <h1 class="text-4xl font-extrabold mb-8">Dashboard Admin</h1>

        <!-- Section Utilisateurs -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <!-- Total Inscrits -->
            <div
                class="p-6 bg-primary-light rounded-xl shadow-lg flex flex-col items-center transition-transform hover:scale-105 relative">
                <p class="text-primary-dark font-semibold mb-2">Total d'inscriptions</p>
                <p class="text-3xl font-bold text-primary-dark">{{ totalUsers }}</p>

                <!-- Nouveau utilisateurs aujourd'hui -->
                <template v-if="newUsersToday > 0">
                    <div class="absolute top-2 right-4 flex items-center gap-1 text-sm text-green-600 font-semibold">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" stroke-width="3">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                        </svg>
                        <span>+{{ newUsersToday }}</span>
                    </div>
                </template>
            </div>

            <!-- Total Abonnés -->
            <div
                class="p-6 bg-primary-light rounded-xl shadow-lg flex flex-col items-center transition-transform hover:scale-105">
                <p class="text-primary-dark font-semibold mb-2">Total abonnés</p>
                <p class="text-3xl font-bold text-primary-dark">{{ totalSubscribers }}</p>
            </div>

            <!-- % abonnés -->
            <div
                class="p-6 bg-primary-light rounded-xl shadow-lg flex flex-col items-center transition-transform hover:scale-105">
                <p class="text-primary-dark font-semibold mb-2">% d'utilisateurs abonnés</p>
                <p class="text-3xl font-bold text-primary-dark">
                    {{ totalUsers > 0 ? Math.round((totalSubscribers / totalUsers) * 100) : 0 }}%
                </p>
            </div>

            <!-- Répartition abonnés -->
            <div
                class="p-6 bg-primary-light rounded-xl shadow-lg flex flex-col items-center transition-transform hover:scale-105">
                <p class="text-primary-dark font-semibold mb-2">Répartition abonnés</p>
                <p class="text-lg text-primary-dark">Mensuel : {{ monthlySubscribers }}</p>
                <p class="text-lg text-primary-dark">Annuel : {{ yearlySubscribers }}</p>
            </div>

            <!-- % abonnés mensuel -->
            <div
                class="p-6 bg-primary-light rounded-xl shadow-lg flex flex-col items-center transition-transform hover:scale-105">
                <p class="text-primary-dark font-semibold mb-2">% abonnés mensuel</p>
                <p class="text-3xl font-bold text-primary-dark">
                    {{ totalSubscribers > 0 ? Math.round((monthlySubscribers / totalSubscribers) * 100) : 0 }}%
                </p>
            </div>

            <!-- % abonnés annuel -->
            <div
                class="p-6 bg-primary-light rounded-xl shadow-lg flex flex-col items-center transition-transform hover:scale-105">
                <p class="text-primary-dark font-semibold mb-2">% abonnés annuel</p>
                <p class="text-3xl font-bold text-primary-dark">
                    {{ totalSubscribers > 0 ? Math.round((yearlySubscribers / totalSubscribers) * 100) : 0 }}%
                </p>
            </div>
        </div>

        <!-- Conteneur global -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Section Pages Entreprises -->
            <div>
                <h2 class="text-2xl font-bold text-gray-800 mb-4">Pages Entreprises</h2>
                <div class="space-y-3">
                    <div v-for="info in entrepriseStats" :key="info.label"
                        class="flex items-center border p-4 rounded shadow-md space-x-4">
                        <!-- Nom -->
                        <p class="w-1/3 text-gray-700 font-medium">{{ info.label }}</p>

                        <!-- Barre -->
                        <div class="w-2/3">
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                                <div :style="{ width: info.percent + '%' }" :class="[
                                    'h-2 rounded-full',
                                    info.percent < 30 ? 'bg-red-500' : info.percent < 70 ? 'bg-yellow-500' : 'bg-green-500'
                                ]"></div>
                            </div>
                            <p class="text-sm text-gray-500 mt-1">{{ info.percent }}%</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Section Réseaux sociaux -->
            <div>
                <h2 class="text-2xl font-bold text-gray-800 mb-4">Réseaux sociaux renseignés</h2>
                <div class="space-y-3">
                    <div v-for="network in networkStats" :key="network.label"
                        class="flex items-center border p-4 rounded shadow-md space-x-4">
                        <!-- Nom -->
                        <p class="w-1/3 text-gray-700 font-medium">{{ network.label }}</p>

                        <!-- Barre -->
                        <div class="w-2/3">
                            <div class="w-full h-2 bg-gray-200 rounded-full">
                                <div :style="{ width: network.percent + '%' }" :class="[
                                    'h-2 rounded-full',
                                    network.percent < 30 ? 'bg-red-500' : network.percent < 70 ? 'bg-yellow-500' : 'bg-green-500'
                                ]"></div>
                            </div>
                            <p class="text-sm text-gray-500 mt-1">{{ network.percent }}%</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <section class="max-w-md mx-auto mt-10 p-6 bg-white rounded-xl shadow-md">
            <h2 class="text-xl font-bold mb-4 text-gray-800">Activer un abonnement</h2>

            <form @submit.prevent="activerAbonnement" class="space-y-4">
                <!-- Champ ID utilisateur -->
                <div>
                    <label for="mail" class="block text-gray-700 font-medium mb-1">mail utilisateur</label>
                    <input v-model="mail" type="mail" id="mail" placeholder="Entrez le mail de l'utilisateur"
                        class="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring focus:ring-blue-300" />
                </div>

                <!-- Bouton -->
                <button type="submit"
                    class="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition">
                    Activer l'abonnement
                </button>
            </form>

            <!-- Message -->
            <p v-if="message" class="mt-4 text-center" :class="isError ? 'text-red-500' : 'text-green-600'">
                {{ message }}
            </p>
        </section>

    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

// URL de base pour les appels à l'API backend (adapter selon l'environnement)
const baseUrl = process.env.VUE_APP_BASE_URL

const mail = ref("")
const message = ref("")
const isError = ref(false)

const token = localStorage.getItem('adminToken')

// Statistiques utilisateurs
const totalUsers = ref(0)
const totalSubscribers = ref(0)
const monthlySubscribers = ref(0)
const yearlySubscribers = ref(0)
const newUsersToday = ref(0)

// Statistiques entreprises et réseaux sociaux
const entrepriseStats = ref([])
const networkStats = ref([])

const activerAbonnement = async () => {
    if (!mail.value) {
        message.value = "Veuillez entrer un mail utilisateur."
        isError.value = true
        return
    }

    try {
        const res = await fetch(`${baseUrl}/api/admin/activer-abonnement`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ email: mail.value }), // <-- on envoie le mail
        })

        if (!res.ok) throw new Error("Erreur lors de l’activation")

        const data = await res.json()
        message.value = data.message
        isError.value = false
        mail.value = ""
    } catch (err) {
        console.error(err)
        message.value = "❌ Échec de l’activation"
        isError.value = true
    }
}


onMounted(async () => {
    try {
        const res = await fetch(`${baseUrl}/api/admin/dashboard`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });
        const data = await res.json()

        totalUsers.value = data.totalUsers
        totalUsers.value = data.totalUsers
        totalSubscribers.value = data.totalSubscribers
        monthlySubscribers.value = data.monthlySubscribers
        yearlySubscribers.value = data.yearlySubscribers
        newUsersToday.value = data.newUsersToday
        entrepriseStats.value = data.entrepriseStats
        networkStats.value = data.networkStats
    } catch (error) {
        console.error("Erreur de chargement du dashboard admin:", error)
    }
})
</script>
